import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faEye } from '@fortawesome/pro-solid-svg-icons';

import { SecurityService } from '../../security/security.service';
import { profileIsViewOnly } from '../../security/permission-profiles';

/**
 * S2N-3076: the one persistent cue that the signed-in user holds the View Only profile.
 *
 * A View Only user sees what an Admin sees inside the agency, so every screen looks normal
 * until a Save is refused. Rather than explain that on each form, the shell renders this bar
 * once, in flow above the routed page. It renders nothing for every other profile.
 *
 * Reactive on `security$` (not a one-shot `getSecurity()`): a 401 re-auth or a swap-client
 * can change the profile mid-session, and the bar has to follow without a reload. Not an
 * authorization boundary — the server refuses every View Only write regardless.
 *
 * It does NOT show on the user's own account pages. The copy says nothing here can be changed, and
 * on their own record that is false: own-account security is the set of writes this profile keeps
 * (signature, PIN, password, MFA), and S2N-3166's custom field values on the details tab go with
 * it. A bar contradicting the form under it is worse than no bar.
 */
@Component({
    selector: 's2-view-only-banner'
    , imports: [ FontAwesomeModule ]
    , template: `
        @if (showBanner()) {
            <div
                class="s2-view-only-banner"
                role="status"
            >
                <fa-icon
                    [icon]="faEye"
                    class="s2-view-only-banner-icon"
                    aria-hidden="true"
                ></fa-icon>
                <span>View Only &mdash; you can see everything in this agency, but nothing here can be changed.</span>
            </div>
        }
    `
    , styles: `
        /* The shell .main-container is a flex ROW (scss/layout/_nav.scss), so an in-flow block here
           shrinks to its content and the routed page lines up beside it. Taking the full row (the
           container wraps) keeps the bar across the top with the page underneath. */
        :host {
            display: block;
            flex: 0 0 100%;
            width: 100%;
            /* Above the routed page: the dashboard overlaps this row (see body.s2-view-only). */
            position: relative;
            z-index: 3;
        }

        .s2-view-only-banner {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            width: 100%;
            /* Sized to the variable the shell publishes on body.s2-view-only, so pages can reserve
               exactly this much room. Content is ~1.95rem tall and centres inside it. */
            height: var(--s2-view-only-banner-height, auto);
            box-sizing: border-box;
            padding: 0.35rem 1rem;
            font-size: 0.875rem;
            line-height: 1.25rem;
            color: var(--alert-info-text);
            background-color: var(--alert-info-bg);
            border-bottom: 1px solid var(--alert-info-border);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            margin-bottom: 1rem;
        }

        .s2-view-only-banner-icon { flex: 0 0 auto; }

        .s2-view-only-banner span {
            overflow: hidden;
            text-overflow: ellipsis;
        }

        @media print { .s2-view-only-banner { display: none !important; } }
    `
})
export class ViewOnlyBannerComponent {

    sec = inject(SecurityService);
    private router = inject(Router);

    faEye = faEye;

    isViewOnly = toSignal(
        this.sec.security$.pipe(map(sec => profileIsViewOnly(sec?.permissionProfile)))
        , { initialValue: false }
    );

    /**
     * The account pages a user may write regardless of profile, as url prefixes. Each is followed
     * by the user id, which is checked separately — the same path with SOMEBODY ELSE's id is
     * read-only for this profile and does want the bar.
     */
    private static readonly ownAccountPaths = [
        '/admin/users/edit/details/'
        , '/admin/users/edit/security/'
    ];

    /** startWith so the first paint is right; the shell mounts this before the first navigation. */
    private url = toSignal(
        this.router.events.pipe(
            filter(e => e instanceof NavigationEnd)
            , map(e => (e as NavigationEnd).urlAfterRedirects)
            , startWith(this.router.url)
        )
        , { initialValue: this.router.url }
    );

    /**
     * Whether the page on screen is the signed-in user's OWN account page. The id has to match:
     * a View Only user opens other people's records at the same paths (the reads are marked
     * [AllowAgencyRead]) and those are read-only, so the bar belongs there.
     */
    private isOwnAccountPage = computed<boolean>(() => {
        const id = this.sec.getUser()?.id;
        if (id == null) { return false; }
        // Query string and fragment stripped: the details page is reached with ?isCreatingNewUser
        // and the security page with ?r=1, and neither changes whose record it is.
        const path = (this.url() ?? '').split('?')[0].split('#')[0];
        return ViewOnlyBannerComponent.ownAccountPaths.some(prefix =>
            path.startsWith(prefix)
            && path.slice(prefix.length) === String(id)
        );
    });

    showBanner = computed<boolean>(() => this.isViewOnly() && !this.isOwnAccountPage());
}
